// MaintenanceSelect.js
import React, { useMemo } from 'react';
import { FILTER_CONFIG, UI_TYPES } from './FilterConfig';
import { EntityPickerField } from '../../../common/selectComponents/EntityPicker/EntityPickerField';
import { EntityPickerSelect } from '../../../common/selectComponents/EntityPicker/EntityPickerSelect';
// ADDED: Imports for new filter types
import { DateInput } from '../../../common/Input';
import { Select } from '../../../common/Select';
import { MonthPickerField } from '../../../common/selectComponents/MonthPicker/MonthPickerField';
import { YearPickerField } from '../../../common/selectComponents/YearPicker/YearPickerField';
import { FEATURES } from '../../../config/features';

/**
 * MaintenanceSelect - Factory Component
 * Renders the appropriate filter UI based on the FilterConfig.
 */
export const MaintenanceSelect = ({ type, value, onChange, activeFilterState, allowAll }) => {
  const config = FILTER_CONFIG[type];

  // 1. Generic Dependency Logic
  const hasDependencies = config.dependsOn && config.dependencyMap;

  // Check if any required dependency is missing a value
  const isMissingDependency = config.dependsOn?.some(depKey => !activeFilterState[depKey]?.id);
  const disableFilter = !!config.dependsOn && isMissingDependency;

  // 2. Build the Dynamic API Endpoint
  const updatedConfig = useMemo(() => {
    if (!hasDependencies || isMissingDependency) return config;

    // Build query string: ?employeeTypeId=1&divisionId=2
    const params = new URLSearchParams();

    // Always include these for Department if your UI needs the labels
    if (type === "DEPARTMENT" || type === "DEPARTMENT_MSC_TOGGLE") {
      const useDimDivisionDepartmentValue = FEATURES.USE_DIM_DIVISION_DEPARTMENT ? "true" : "false";
      const useDimEmployeeTypeDepartment = FEATURES.USE_DIM_DIVISION_DEPARTMENT ? "true" : "false";

      params.append("includeDivision", useDimDivisionDepartmentValue);
      params.append("includeEmployeeType", useDimEmployeeTypeDepartment);
    }

    Object.entries(config.dependencyMap).forEach(([stateKey, apiKey]) => {
      const selectedItem = activeFilterState[stateKey];
      if (selectedItem?.id) {
        params.append(apiKey, selectedItem.id);
      }
    });

    // Remove the trailing part of the endpoint if it was hardcoded and append params
    const baseEndpoint = config.pickerConfig.apiEndpoint.split('?')[0];

    return {
      ...config,
      pickerConfig: {
        ...config.pickerConfig,
        apiEndpoint: `${baseEndpoint}?${params.toString()}`
      }
    };
  }, [config, activeFilterState, type, isMissingDependency, hasDependencies]);

  if (!config) return null;

  // --- ADDED: BACKWARD COMPATIBLE DISPLAY VALUE LOGIC ---
  // We use updatedConfig.pickerConfig to find which field to show (default: "description")
  const primaryField = updatedConfig?.pickerConfig?.displayFields?.primary || "description";
  const displayValue = value ? value[primaryField] : "";
  // ------------------------------------------------------

  // 4. Render Engine based on UI_TYPE
  const renderFilterControl = () => {
    switch (config.uiType) {
      case UI_TYPES.PICKER:
        return (
          <EntityPickerField
            config={updatedConfig.pickerConfig}
            entityId={value?.id}
            displayValue={displayValue}
            onSelect={(item) => onChange(item)}
            onClear={() => onChange(null)}
            placeholder={allowAll ? "All" : "--Selecte Data--"}
            isRequired={false}
            allowClear={true}
            disabled={disableFilter}
          />
        );

      case UI_TYPES.DROPDOWN:
        return (
          <EntityPickerSelect
            config={updatedConfig.pickerConfig}
            value={value}
            onChange={(item) => onChange(item)}
            placeholder={allowAll === true ? "All" : "--Select Data--"}
            className="h-[34px] text-sm border-gray-300 rounded-md shadow-sm"
            disabled={disableFilter}
            allowAll={allowAll}
          />
        );

      case UI_TYPES.DATE: // ADDED: Support for Date Input
        return (
          <DateInput
            value={value || ""} // value is a raw string "YYYY-MM-DD"
            onChange={(e) => onChange(e.target.value)}
            className="h-[34px] !py-1"
            isDisabled={disableFilter}
          />
        );

      case UI_TYPES.SELECT_STATIC: // ADDED: Support for Static Dropdowns (not from DB)
        return (
          <Select
            options={config.options || []}
            value={value?.id || value || ""} // Handles both object and primitive state
            onChange={(e) => onChange(e.target.value)}
            placeholder="-- All --"
            className="h-[34px] text-sm"
            disabled={disableFilter}
            allowSelectAll={allowAll}
          />
        );

      case UI_TYPES.TEXT: // ADDED: Support for raw text search
        return (
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Search..."
            className="w-full border border-gray-300 rounded-md px-3 h-[34px] text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
            disabled={disableFilter}
          />
        );

      case UI_TYPES.MONTH: // ADDED: Support for raw text search
        return (
          <MonthPickerField
            value={value}
            onSelect={(monthName) => onChange(monthName)}
            onClear={() => onChange(null)}
            placeholder={allowAll === true ? "All" : "--Select Month--"}
            disabled={disableFilter}
            isRequired={false}
          />
        );

      case UI_TYPES.YEAR: // ADDED: Support for raw text search
        return (
          <YearPickerField
            value={value}
            onSelect={(year) => onChange(year)}
            onClear={() => onChange(null)}
            placeholder={allowAll === true ? "All" : "--Select Year--"}
            disabled={disableFilter}
            isRequired={false}
          />
        );

      default:
        return <div className="text-xs text-red-500">Invalid UI Type</div>;
    }
  };

  return (
    <div className="flex flex-col w-full">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {config.label}
      </label>

      <div className="w-full">
        {renderFilterControl()}
      </div>
    </div>
  );
};